import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fmt, type ReportsPayload } from '../../lib/api';
import type { Drill } from '../../pages/Reports';
import { COLORS } from './utils';

export default function CarrierTrend({
  data, drill, setDrill,
}: { data: ReportsPayload; drill: Drill; setDrill: (d: Drill) => void }) {
  const carriers = useMemo(
    () => (drill?.kind === 'carrier' ? data.carriers.filter((c) => c === drill.id) : data.carriers),
    [data.carriers, drill],
  );
  const chartData = useMemo(
    () =>
      data.periods.map((p) => {
        const row: Record<string, number | string> = { period: p.period_label };
        for (const c of carriers) row[c] = data.byCarrier[c]?.[p.pay_run_id]?.earned ?? 0;
        return row;
      }),
    [data, carriers],
  );

  const lastTwo = data.periods.slice(-2);

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-medium">By carrier — earned</h2>
        {drill?.kind === 'carrier' && (
          <button
            className="text-xs underline"
            style={{ color: 'var(--color-text-muted)' }}
            onClick={() => setDrill(null)}
          >
            clear filter
          </button>
        )}
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis dataKey="period" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmt.money(v)} />
          <Tooltip formatter={(v) => (typeof v === 'number' ? fmt.money(v) : String(v ?? ''))} />
          <Legend />
          {carriers.map((c, i) => (
            <Line key={c} type="monotone" dataKey={c} stroke={COLORS[i % COLORS.length]} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>

      <div className="overflow-x-auto mt-3">
        <table className="table w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: 'var(--color-table-header)' }}>
              <th className="py-1 text-left">Carrier</th>
              {data.periods.map((p) => (
                <th key={p.pay_run_id} className="py-1 text-right">{p.period_label}</th>
              ))}
              <th className="py-1 text-right">Δ vs prior</th>
            </tr>
          </thead>
          <tbody>
            {data.carriers.map((c) => {
              const valAt = (rid: string) => data.byCarrier[c]?.[rid]?.earned ?? 0;
              const prev = lastTwo.length === 2 ? valAt(lastTwo[0].pay_run_id) : null;
              const cur = lastTwo.length ? valAt(lastTwo[lastTwo.length - 1].pay_run_id) : 0;
              const delta = prev === null ? null : cur - prev;
              return (
                <tr
                  key={c}
                  className="cursor-pointer hover:bg-opacity-50"
                  style={{ borderTop: '1px solid var(--color-border)' }}
                  onClick={() => setDrill({ kind: 'carrier', id: c })}
                >
                  <td className="py-1">{c}</td>
                  {data.periods.map((p) => (
                    <td key={p.pay_run_id} className="py-1 text-right font-mono">
                      {fmt.money(valAt(p.pay_run_id))}
                    </td>
                  ))}
                  <td
                    className="py-1 text-right font-mono"
                    style={
                      delta === null
                        ? { color: 'var(--color-text-muted)' }
                        : delta >= 0
                        ? { color: 'var(--color-success, #16a34a)' }
                        : { color: 'var(--color-danger, #dc2626)' }
                    }
                  >
                    {delta === null ? '—' : (delta >= 0 ? '+' : '') + fmt.money(delta)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
